import {
  CalculateDocumentVariableAsyncEventArgs,
  DXR,
  FieldApi,
  IntervalApi,
  RichEdit,
  SubDocumentApi,
} from 'components/devexpress/RichEdit/dx-richedit';
import { TableDataCellProperties, VariableDto } from 'services/api/api-client';
import { TableWidthTypeApi } from 'devexpress-richedit/lib/client/model-api/table/enums';
import { IInterval } from 'devexpress-richedit/lib/client/model-api/interval';
import { TableApi } from 'devexpress-richedit/lib/client/model-api/table/table';
import {
  applyCellsProperties,
  MergedCells,
  mergeTableCells,
} from './table-data-helper.ts';
import { QueryFactory } from 'services/api';
import { extractImages, reinsertExtractedImages } from './image-helper.ts';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';
import i18n from 'i18next';
import { findNestedVariableMarkers } from './nested-variable-markers';

export const simpleHtmlRegex = /<(.*)>/g;

/*
  Creates a table like below:
  -------------------------------------------------
  prop1          |  prop2          | prop3
  -------------------------------------------------
  data[0].prop1  |  data[0].prop2  | data[0].prop3
  -------------------------------------------------
  data[1].prop1  |  data[1].prop2  | data[1].prop3
  -------------------------------------------------
  data[2].prop1  |  data[2].prop2  | data[2].prop3
  -------------------------------------------------
  ...
  Each item of `data` array represents a ROW.
  Headers are added with names according to property names.

  We assume all `data` elements have the same properties!
  You could pass this list of properties as `properties` parameter,
  or pass undefined and we will just use  Object.keys(data[0])
 */
export function createTableFromArrayOfRows(
  richEdit: RichEdit,
  data: Record<string, any>[],
  properties: string[],
  mergedCells?: MergedCells | null, // Format: {rowIndex: {startCellIndex: endCellIndex}}.
  cellsProperties?: TableDataCellProperties[] | null,
) {
  const processor = richEdit.createDocumentProcessor();
  const doc = processor.document;
  if (
    properties.length === 0 ||
    (data.length === 0 && properties.length === 0)
  ) {
    // empty table
    doc.tables.create(0, 1, 1);
    return processor;
  }

  const table = doc.tables.create(0, properties.length, data.length);
  table.autoFit = false;

  const currentCursorPosition = richEdit.selection.active;
  const currentSection = richEdit.document.sections.find(currentCursorPosition);
  const availableWidthTwips =
    currentSection!.pageSize.width -
    currentSection!.margins.left -
    currentSection!.margins.right;
  table.width = { type: TableWidthTypeApi.Twips, value: availableWidthTwips }; // setting table to full editor width
  const columnsWidthTwips = getColumnWidthTwips(
    data,
    properties,
    availableWidthTwips,
    1500,
  );

  // add data rows
  for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
    const row = table.rows.getByIndex(rowIndex)!;
    for (let colIndex = 0; colIndex < properties.length; colIndex++) {
      const cell = row.cells.getByIndex(colIndex)!;

      const twips = columnsWidthTwips[colIndex];
      cell.width = {
        type: TableWidthTypeApi.Twips,
        value: twips,
      };

      const text = data[rowIndex][properties[colIndex]]?.toString();
      doc.insertText(cell.interval.start, text?.replaceAll('\r', '') ?? '');
    }
  }

  const lastRow = table.rows.getByIndex(data.length - 1);
  for (let colIndex = 0; colIndex < properties.length; colIndex++) {
    const cell = lastRow!.cells.getByIndex(colIndex)!;
    doc.insertText(cell.interval.start, '');
  }

  if (mergedCells) mergeTableCells(table, mergedCells);
  if (cellsProperties) applyCellsProperties(table, cellsProperties);

  return processor;
}

/**
 * Creates plain text representation of table and returns document processor with it.
 */
export async function createPlainTextFromTableValue(
  richEdit: RichEdit,
  variableValue: string,
) {
  const processor = richEdit.createDocumentProcessor();
  const doc = processor.document;

  const currentCursorPosition = richEdit.selection.active;
  const currentSection = richEdit.document.sections.find(currentCursorPosition);
  const isLandscape = currentSection.landscape;
  const fontSize = richEdit.document.getDefaultCharacterProperties().size;
  const fontName = richEdit.document.getDefaultCharacterProperties().fontName;

  const plainTextTableHtml =
    await QueryFactory.DocumentUtilitiesClient.getPlainTextTable({
      variableValue,
      isLandscapeOrientation: isLandscape,
      fontSize,
      fontName,
    });

  doc.insertText(0, plainTextTableHtml);

  return processor;
}

/**
 * Iterates through all document variables and inserts their contents as HTML, if it is present in variable value.
 */
export function parseHtmlVariables(richEdit: RichEdit) {
  const doc = richEdit.selection.activeSubDocument;
  for (let fieldIndex = 0; fieldIndex < doc.fields.count; fieldIndex++) {
    const field = doc.fields.getByIndex(fieldIndex)!;

    const fieldCode = doc.getText(field.codeInterval);
    if (!fieldCode.includes('DOCVARIABLE')) continue;

    const fieldText = doc.getText(field.resultInterval);
    if (!fieldText.match(simpleHtmlRegex)) continue;

    const table = doc.tables.find(field.interval)[0];
    if (table) {
      parseHtmlInTableField(doc, table, field.resultInterval);
    } else {
      parseHtmlInPlainField(doc, fieldText, field.resultInterval);
    }
  }
}

function parseHtmlInPlainField(
  doc: SubDocumentApi,
  fieldText: string,
  fieldInterval: IntervalApi,
) {
  const interval = searchSubstring(doc, fieldText, fieldInterval);
  if (!interval) return;

  // Pull data-URI images out before they reach insertHtml.
  const { html: safeHtml, images } = extractImages(fieldText);

  try {
    doc.insertHtml(interval.end as number, safeHtml);
    doc.deleteText(interval as any);
  } catch (e) {
    console.error('parseHtmlInPlainField: insertHtml failed', e);
    return;
  }

  if (images.length > 0) {
    reinsertExtractedImages(doc, fieldInterval, images);
  }
}

function parseHtmlInTableField(
  doc: SubDocumentApi,
  table: TableApi,
  fieldInterval: IntervalApi,
) {
  for (let rowIndex = 0; rowIndex < table.rows.count; rowIndex++) {
    const row = table.rows.getByIndex(rowIndex)!;

    for (let colIndex = 0; colIndex < row.cells.count; colIndex++) {
      const cell = row.cells.getByIndex(colIndex)!;
      const cellText = doc.getText(cell.interval);

      if (cellText.match(simpleHtmlRegex)) {
        const interval = searchSubstring(doc, cellText, fieldInterval);
        if (!interval) continue;

        const { html: safeHtml, images } = extractImages(cellText);

        try {
          // Without decreasing by 1 html is inserting to the next cell.
          doc.insertHtml((interval.end as number) - 1, safeHtml);
          doc.deleteText({
            start: interval.start as number,
            length: cellText.length - 1,
          });
        } catch (e) {
          console.error('parseHtmlInTableField: insertHtml failed', e);
          continue;
        }

        if (images.length > 0) {
          reinsertExtractedImages(doc, fieldInterval, images);
        }
      }
    }
  }
}

/**
 * Replaces nested variable markers in specified field (e.g. {DOCVARIABLE 23}).
 */
export async function replaceNestedVariableMarkers(
  richEdit: RichEdit,
  revisionId: number,
  field: FieldApi,
) {
  const document = field.subDocument;

  const variableCache: Record<number, VariableDto> = {};
  const getVariableValue = async (id: number) => {
    if (variableCache[id]) return variableCache[id];
    const variable = await QueryFactory.VariableClient.get(id, revisionId);
    variableCache[id] = variable;
    return variable;
  };

  const fieldCode = document.getText(field.codeInterval);
  if (!fieldCode.includes('DOCVARIABLE')) return;

  const table = document.tables.find(field.interval)[0];
  if (!table) return;

  await replaceNestedVariableMarkersInTable(
    richEdit,
    document,
    table,
    getVariableValue,
  );
}

async function replaceNestedVariableMarkersInTable(
  richEdit: RichEdit,
  document: SubDocumentApi,
  table: TableApi,
  getVariableValue: (id: number) => Promise<VariableDto>,
) {
  for (let rowIndex = 0; rowIndex < table.rows.count; rowIndex++) {
    const row = table.rows.getByIndex(rowIndex)!;

    for (let colIndex = 0; colIndex < row.cells.count; colIndex++) {
      const cell = row.cells.getByIndex(colIndex)!;
      const fields = Array.from(
        { length: document.fields.count },
        (_, index) => document.fields.getByIndex(index)!.interval,
      );
      const matches = findNestedVariableMarkers(
        cell.interval,
        fields,
        (range) =>
          document.getText(new DXR.Interval(range.start, range.length)),
      );
      if (matches.length === 0) continue;

      // Replacing from the last match to the first keeps earlier offsets in the cell valid.
      for (const match of matches) {
        const nestedVariableId = match.variableId;
        const markerInterval = new DXR.Interval(match.start, match.length);

        const variable = await getVariableValue(nestedVariableId);

        if (variable.isTable) {
          showErrorToast(
            i18n.t('Server_Errors.Nested_Variable_Is_Table', {
              ids: nestedVariableId,
            }),
          );
          continue;
        }

        document.deleteText(markerInterval as any);

        const nestedField = document.fields.create(
          markerInterval.start as number,
          `DOCVARIABLE ${nestedVariableId}`,
        );

        await resolveDocVariableField(
          richEdit,
          nestedField,
          variable.actualValue.value,
        );
      }
    }
  }
}

/**
 * Triggers a DOCVARIABLE field calculation and resolves it with a plain text value.
 */
function resolveDocVariableField(
  richEdit: RichEdit,
  field: FieldApi,
  value: string,
) {
  return new Promise<void>((resolve) => {
    const handler = (
      _richEdit: RichEdit | undefined,
      e: CalculateDocumentVariableAsyncEventArgs | undefined,
    ) => {
      e?.data.forEach((docVariable) => docVariable.callback(value));
      richEdit.events.calculateDocumentVariableAsync.removeHandler(handler);
    };

    richEdit.events.calculateDocumentVariableAsync.addHandler(handler);
    field.update(() => resolve());
  });
}

export function decreaseIndentInTable(
  richEdit: RichEdit,
  tableInterval: IInterval,
) {
  richEdit.selection.setSelection(tableInterval);
  richEdit.executeCommand(DXR.ContextMenuCommandId.DecreaseParagraphIndent);
}

export function decreaseAllTableVariablesIndent(richEdit: RichEdit) {
  const fields = richEdit.document.fields;
  const document = richEdit.document;

  for (let i = 0; i < fields.count; i++) {
    const currentField = fields.getByIndex(i);
    if (
      !currentField ||
      !document.getText(currentField.codeInterval).includes('DOCVARIABLE')
    )
      continue;

    const table = document.tables.find(currentField.interval)[0];
    if (table?.interval) {
      // Check if this is table variable, not variable in table
      if (table.interval.start >= currentField.interval.start) {
        decreaseIndentInTable(richEdit, table.interval);
      }
    }
  }
}

/**
 * Searches for a substring in a document. Required, because there is no such method in the API.
 */
function searchSubstring(
  doc: SubDocumentApi,
  substring: string,
  interval: IntervalApi,
): Interval | null {
  const text = doc.getText(interval);
  const index = text.indexOf(substring);

  if (index === -1) return null;

  const temp = new DXR.Interval(interval.start + index, substring.length);
  if (doc.getText(temp) === substring) {
    return temp;
  }

  return null;
}

/**
 * Calculate the column width as a twips depending on what part of the total
 * sum of table characters is taken by the sum of characters in the column.
 * @param rows - table rows objects
 * @param columnNames - table columns names
 * @param availableWidthTwips - document available width twips
 * @param minimumTwips - minimum width for every column
 */
function getColumnWidthTwips(
  rows: Record<string, any>[],
  columnNames: string[],
  availableWidthTwips: number,
  minimumTwips: number,
) {
  // skip rows with merged cells
  rows = rows.filter((r) => Object.keys(r).length === columnNames.length);

  const columnsCharCounts = columnNames.map((name) =>
    rows.reduce(
      (sum, row) =>
        sum + (stripHTMLTags(row[name]?.toString() ?? '').length ?? 0),
      0,
    ),
  );

  const totalChars = columnsCharCounts.reduce((a, b) => a + b, 0) || 1;

  let widths = columnsCharCounts.map((count) =>
    Math.ceil((count / totalChars) * availableWidthTwips),
  );

  // setting minimum width to minimumTwips twips
  widths = widths.map((w) => (w < minimumTwips ? minimumTwips : w));
  const sum = widths.reduce((a, b) => a + b, 0);

  return widths.map((w) => Math.round((w / sum) * availableWidthTwips));
}

// Removes HTML tags from string
function stripHTMLTags(str: string) {
  return str.replace(/<[^>]*>/g, '');
}
